// External
import { Component, OnInit, Input, ElementRef, HostListener, Renderer2, Output, EventEmitter, ViewChild } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subject } from 'rxjs';

export enum TooltipPosition {
    TOP = 'top',
    BOTTOM = 'bottom',
    RIGHT = 'right',
    LEFT = 'left'
}

export enum TooltipAlignment {
    CENTER = 'center',
    START = 'start',
    END = 'end'
}

@Component({
    selector: 'ui-popover',
    template: ` <ng-content select="[popover-trigger]"></ng-content>
                <div class="popover-wrapper" #content *ngIf="open"
                    [style.width.px]="width"
                    [style.marginLeft.px]="offset.left"
                    [style.marginTop.px]="offset.top">
                    <ng-content select="[popover-content]"></ng-content>
                </div>`,
    host: {
        '[class.popover]': 'true',
        '[class.open]': 'open'
    }
})

export class UiPopoverComponent implements OnInit {

    readonly onDestroy$: Subject<void> = new Subject<void>();

    trigger; // this is the trigger html element (marked popover-trigger)
    offset: any = {}; // this is required offsets for when popover gets out of viewport

    @Input() open = false;
    @Input() direction = TooltipPosition.BOTTOM;
    @Input() alignment = TooltipAlignment.CENTER; // posible values: start / end / center (default)
    @Input() width = 200; // dimensiunea trebuie sa fie fixa, ca sa putem calcula cand iese din viewport

    // for display on hover on desktopp device only
    @Input() displayOnHover = false;
    @Output() onOpen = new EventEmitter();
    @Output() onClose = new EventEmitter();


    // Scroll into view behaviour
    @Input() scrollIn = false;
    content;
    @ViewChild('content', { static: false }) set contentRf(content: ElementRef) {
        this.content = content;
        if (this.content && this.scrollIn) {
            const coords = this.content.nativeElement.getBoundingClientRect();
            if (coords.bottom > window.innerHeight) {
                this.content.nativeElement.scrollIntoView({behavior: 'smooth', block: 'end'});
            }
        }
    }

    constructor(
        readonly deviceDetectorService: DeviceDetectorService,
        readonly renderer: Renderer2,
        readonly el: ElementRef
    ) {}

    ngOnInit() {
        // daca layout-ul este respectat, primul element va fi intotdeauna triggerul
        this.trigger = this.el.nativeElement.firstElementChild;
        this.renderer.addClass(this.trigger, 'popover-trigger');
        this.renderer.addClass(this.el.nativeElement, this.direction);
        this.renderer.addClass(this.el.nativeElement, this.alignment);

        // check if current device is mobile (to disable hover behaviour)
        this.displayOnHover = this.displayOnHover && !(this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet());
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    closePopover() {
        this.open = false;
        this.onClose.emit(this);

        // Remove handler
        document.removeEventListener('click', this.onElseWhere, false);
    }

    openPopover() {
        // Add handler
        document.addEventListener('click', this.onElseWhere, false);

        this.positionPopover();
        this.open = true;
        this.onOpen.emit(this);
    }

    onElseWhere = $event => {
        if ($event.target !== this.trigger && this.open) {
            this.closePopover();
        }
    };

    // toggle open, on trigger click
    @HostListener('click', ['$event']) onPopoverClick($event) {
        // if hover functionality is on and popover is already open, click is no longer needed
        if (this.displayOnHover && this.open) {
            return;
        }
        if ($event.target === this.trigger && !this.open) {
            this.openPopover();
        } else {
            this.closePopover();
        }
    }

    // close popover on escape
    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
        this.closePopover();
    }

    // mouseEnter - mouseLeave events, for hover functionality
    // when hover is active, it replaces click event
    @HostListener('mouseenter', ['$event']) onPopoverMouseEnter() {
        if (!this.open && this.displayOnHover) {
            this.openPopover();
        }
    }

    @HostListener('mouseleave', ['$event']) onPopoverMouseLeave() {
        if (this.open && this.displayOnHover) {
            this.closePopover();
        }
    }
    // end hover functions


    positionPopover() {
        const parentHeight = this.el.nativeElement.offsetParent.offsetHeight;
        const windowOffset = window.innerWidth - this.trigger.getBoundingClientRect().right;

        if ((this.direction === TooltipPosition.TOP || this.direction === TooltipPosition.BOTTOM)) {
            if (this.alignment === TooltipAlignment.CENTER && windowOffset < this.width / 2) {
                this.offset.left = windowOffset - this.width / 2;
            }

            if (parentHeight - this.el.nativeElement.offsetTop < parentHeight / 2) {
                this.renderer.removeClass(this.el.nativeElement, this.direction);
                this.direction = TooltipPosition.TOP;
                this.renderer.addClass(this.el.nativeElement, this.direction);
            }
        }
    }

}
